/**
 * RAG Connector configuration component.
 * Specialized configuration for RAG connector (RAGFlow).
 */

// React Imports
import { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  TextField,
} from '@mui/material';

export interface RagConnectorConfigProps {
  settingsJson?: string;
  onChange?: (settingsJson: string) => void;
}

/**
 * RAG Connector configuration component.
 */
export const RagConnectorConfig = ({
  settingsJson = '{}',
  onChange,
}: RagConnectorConfigProps) => {
  const [ragFlowUrl, setRagFlowUrl] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [defaultKnowledgeBaseId, setDefaultKnowledgeBaseId] = useState('');
  const [defaultTopK, setDefaultTopK] = useState(5);
  const [defaultScoreThreshold, setDefaultScoreThreshold] = useState(0.7);

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h6" sx={{ mb: 3 }}>
          RAGFlow Configuration
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="RAGFlow Server URL"
            value={ragFlowUrl}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setRagFlowUrl(e.target.value);
              const settings = JSON.parse(settingsJson || '{}');
              settings.ragFlowUrl = e.target.value;
              onChange?.(JSON.stringify(settings));
            }}
            fullWidth
            required
            helperText="Base URL of your RAGFlow server (e.g., https://ragflow.example.com)"
          />
          <TextField
            label="API Key"
            type="password"
            value={apiKey}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setApiKey(e.target.value);
                const settings = JSON.parse(settingsJson || '{}');
                settings.apiKey = e.target.value;
                onChange?.(JSON.stringify(settings));
              }}
            fullWidth
            helperText="RAGFlow API key or bearer token"
          />
        </Box>

        <Box sx={{ mt: 3, pt: 3, borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Knowledge Base Settings
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Default Knowledge Base ID"
              value={defaultKnowledgeBaseId}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setDefaultKnowledgeBaseId(e.target.value);
                const settings = JSON.parse(settingsJson || '{}');
                settings.defaultKnowledgeBaseId = e.target.value;
                onChange?.(JSON.stringify(settings));
              }}
              fullWidth
              helperText="Default knowledge base ID for queries"
            />
            <TextField
              label="Default Top K Results"
              type="number"
              value={defaultTopK}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const value = Number(e.target.value);
                setDefaultTopK(value);
                const settings = JSON.parse(settingsJson || '{}');
                settings.defaultTopK = value;
                onChange?.(JSON.stringify(settings));
              }}
              fullWidth
              helperText="Number of top results to return (default: 5)"
            />
            <TextField
              label="Default Score Threshold"
              type="number"
              inputProps={{ step: 0.1, min: 0, max: 1 }}
              value={defaultScoreThreshold}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const value = Number(e.target.value);
                setDefaultScoreThreshold(value);
                const settings = JSON.parse(settingsJson || '{}');
                settings.defaultScoreThreshold = value;
                onChange?.(JSON.stringify(settings));
              }}
              fullWidth
              helperText="Minimum relevance score (0.0-1.0, default: 0.7)"
            />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};
